import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Copy, Check, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { integrationsApi } from '../../services/integrations-api';
import type { InboundFileSource } from '../../services/integrations-api';
import { handleApiError } from '../../services/api';
import InboundFileReview from './InboundFileReview';
import InboundFileRuns from './InboundFileRuns';

/**
 * Setup page for a file feed: the private address, what it accepts, who may send
 * to it, the learned-map Review tab and the Runs console.
 */

type Tab = 'settings' | 'review' | 'runs';

const TABS: readonly Tab[] = ['settings', 'review', 'runs'];

const TAB_LABEL: Record<Tab, string> = {
  settings: 'Settings',
  review: 'Review mapping',
  runs: 'Runs',
};

/** A spreadsheet and a split console want every pixel; forms stop reading well when wide. */
const WIDE_TABS: readonly Tab[] = ['review', 'runs'];

const InboundFileSetup: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { dataSourceId: routeId } = useParams<{ dataSourceId?: string }>();
  // Reached by a hard navigation from the integrations grid, so the chosen
  // business arrives in the URL rather than in React state.
  const [searchParams] = useSearchParams();
  const targetOrgId = searchParams.get('orgId');
  // Deep links from Source Overview arrive as ?tab=runs&run=<id>.
  const requestedTab = searchParams.get('tab') as Tab | null;
  const initialRunId = searchParams.get('run');

  const [created, setCreated] = useState<InboundFileSource | null>(null);
  const [name, setName] = useState('');
  const [purposeDraft, setPurposeDraft] = useState('');
  const [senders, setSenders] = useState<string[]>([]);
  const [newSender, setNewSender] = useState('');
  const [copied, setCopied] = useState(false);
  // Manage-mode lands on Review (the main event once the address exists); the
  // create flow always starts in Settings.
  const [tab, setTab] = useState<Tab>(() => {
    if (requestedTab && TABS.includes(requestedTab)) return requestedTab;
    return routeId ? 'review' : 'settings';
  });

  // Manage an existing source if we arrived with an id; otherwise this is the create flow.
  const sourceQuery = useQuery({
    queryKey: ['inbound-file-source', routeId],
    queryFn: () => integrationsApi.getInboundFileSource(routeId as string),
    enabled: !!routeId,
  });

  const source: InboundFileSource | null = created ?? sourceQuery.data ?? null;
  const activeId = source?.id ?? null;

  // Seed local editable state only when the source identity changes — keying on
  // `source.id` (not the whole object) so a background refetch can't clobber the
  // user's unsaved purpose/sender edits.
  useEffect(() => {
    if (source) {
      setSenders(source.allowedSenders ?? []);
      setPurposeDraft(source.summaryPurpose ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.id]);

  const createMutation = useMutation({
    mutationFn: () =>
      integrationsApi.createInboundFileSource(name.trim(), purposeDraft.trim(), targetOrgId),
    onSuccess: (data) => {
      setCreated(data);
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      void queryClient.invalidateQueries({ queryKey: ['connectedIntegrations'] });
      toast.success('File feed created');
    },
    onError: (error) => toast.error(handleApiError(error) || 'Failed to create source'),
  });

  const savePurposeMutation = useMutation({
    mutationFn: () =>
      integrationsApi.updateInboundFilePurpose(activeId as string, purposeDraft.trim()),
    onSuccess: () => toast.success('Purpose saved'),
    onError: (error) => toast.error(handleApiError(error) || 'Failed to save purpose'),
  });

  const addSenderMutation = useMutation({
    mutationFn: () => integrationsApi.addInboundFileSender(activeId as string, newSender.trim()),
    onSuccess: (data) => {
      setSenders(data.allowedSenders);
      setNewSender('');
      toast.success('Sender added');
    },
    onError: (error) => toast.error(handleApiError(error) || 'Failed to add sender'),
  });

  const removeSenderMutation = useMutation({
    mutationFn: (email: string) => integrationsApi.removeInboundFileSender(activeId as string, email),
    onSuccess: (data) => {
      setSenders(data.allowedSenders);
      toast.success('Sender removed');
    },
    onError: (error) => toast.error(handleApiError(error) || 'Failed to remove sender'),
  });

  const handleCopy = async () => {
    if (!source) return;
    try {
      await navigator.clipboard.writeText(source.inboundAddress);
      setCopied(true);
      toast.success('Forwarding address copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy address');
    }
  };

  const handleAddSender = () => {
    const email = newSender.trim();
    if (!email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    addSenderMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-background">
      <div
        className={`mx-auto px-4 py-6 sm:px-6 ${
          WIDE_TABS.includes(tab) ? 'max-w-7xl' : 'max-w-5xl'
        }`}
      >
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/sources/integrations')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Integrations
          </Button>
          <h1 className="text-3xl font-bold text-foreground mb-2">Inbound File Feed</h1>
          <p className="text-muted-foreground">
            Email recurring exports — CSV, Excel, PDF or Word — to a private address. Zamino learns
            the format once, you approve it, and every future file imports automatically.
          </p>
        </div>

        {/* CREATE */}
        {!source && (
          <div className="bg-card rounded-lg shadow p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Name<span className="text-destructive ml-1">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g. Weekly timesheets"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <p className="mt-1 text-sm text-muted-foreground">
                Just a label so you can tell your inbound sources apart.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                What kind of files will you forward here?
                <span className="text-destructive ml-1">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 bg-card border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[88px]"
                placeholder="e.g. Weekly staff timesheet export from our scheduling tool, one row per shift."
                value={purposeDraft}
                onChange={(e) => setPurposeDraft(e.target.value)}
              />
              <p className="mt-1 text-sm text-muted-foreground">
                We use this to decide whether a forwarded file belongs here. Be specific — vague
                descriptions let irrelevant files through (or park good ones for review).
              </p>
            </div>

            <div className="bg-accent border border-border rounded-lg p-4 text-sm text-foreground">
              Your email is added to the allowed-sender list automatically. You can add teammates or
              vendors after.
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => createMutation.mutate()}
                disabled={!name.trim() || !purposeDraft.trim() || createMutation.isPending}
                isLoading={createMutation.isPending}
              >
                Create forwarding address
              </Button>
            </div>
          </div>
        )}

        {/* MANAGE */}
        {routeId && sourceQuery.isLoading && (
          <div className="bg-card rounded-lg shadow p-6 text-muted-foreground">Loading…</div>
        )}

        {source && (
          <div className="bg-card rounded-lg shadow p-6">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <span className="text-lg font-semibold text-foreground">{source.name ?? name}</span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-success/15 text-success">
                <span className="w-1.5 h-1.5 rounded-full bg-success" />
                Active
              </span>
            </div>

            <div className="flex gap-1 border-b border-border mb-2">
              {TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 ${
                    tab === t
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {TAB_LABEL[t]}
                </button>
              ))}
            </div>

            {tab === 'settings' && (
            <div className="divide-y divide-border">
            {/* Forwarding address */}
            <div className="py-5">
              <h3 className="text-sm font-semibold text-foreground mb-1">Forwarding address</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Forward (or auto-forward) your recurring exports to this address.
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-4 py-3 bg-muted border border-input rounded-md font-mono text-sm break-all">
                  {source.inboundAddress}
                </div>
                <Button variant="secondary" size="sm" onClick={handleCopy} className="shrink-0">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-info/15 border border-info/20 rounded-lg p-4 mt-3 text-sm text-info">
                <strong>Tip:</strong> set a forwarding rule in Gmail/Outlook so scheduled exports
                arrive here automatically. Auto-forwarded mail keeps the original sender — add those
                addresses below so they aren't held for review.
              </div>
            </div>

            {/* Purpose */}
            <div className="py-5">
              <h3 className="text-sm font-semibold text-foreground mb-1">Purpose</h3>
              <p className="text-sm text-muted-foreground mb-3">
                What this source should accept. Editable any time.
              </p>
              <textarea
                className="w-full px-4 py-3 bg-card border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[80px]"
                value={purposeDraft}
                onChange={(e) => setPurposeDraft(e.target.value)}
              />
              <div className="mt-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => savePurposeMutation.mutate()}
                  disabled={!purposeDraft.trim() || savePurposeMutation.isPending}
                  isLoading={savePurposeMutation.isPending}
                >
                  Save purpose
                </Button>
              </div>
            </div>

            {/* Allowed senders */}
            <div className="py-5">
              <h3 className="text-sm font-semibold text-foreground mb-1">Allowed senders</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Only emails from these addresses are processed. Anything else is parked for review and
                never added to your books.
              </p>

              <div className="space-y-2 mb-3">
                {senders.length === 0 && (
                  <p className="text-sm text-muted-foreground">No senders yet.</p>
                )}
                {senders.map((email) => (
                  <div
                    key={email}
                    className="flex items-center justify-between bg-muted border border-border rounded-md px-3 py-2"
                  >
                    <span className="text-sm text-foreground break-all">{email}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${email}`}
                      onClick={() => removeSenderMutation.mutate(email)}
                      disabled={removeSenderMutation.isPending}
                      className="text-muted-foreground hover:text-destructive p-1 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="name@company.com"
                    value={newSender}
                    onChange={(e) => setNewSender(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleAddSender();
                    }}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAddSender}
                  disabled={!newSender.trim() || addSenderMutation.isPending}
                  isLoading={addSenderMutation.isPending}
                  className="shrink-0"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add sender
                </Button>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <Button onClick={() => navigate('/sources/integrations')}>Done</Button>
            </div>
            </div>
            )}

            {tab === 'review' && activeId && <InboundFileReview dataSourceId={activeId} />}

            {tab === 'runs' && activeId && (
              <InboundFileRuns
                dataSourceId={activeId}
                initialRunId={initialRunId}
                onReviewMapping={() => setTab('review')}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InboundFileSetup;
